import { FormEvent, useState } from "react"
import { Head, Link, router, useForm, usePage } from "@inertiajs/react"
import { AccountShell } from "@/components/AccountShell"
import { PageHeader } from "@/components/PageHeader"
import { LicenseStatusBadge } from "@/components/LicenseStatusBadge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { CopyField } from "@/components/ui/copy-field"
import { SubNav } from "@/components/ui/sub-nav"
import type { PageProps } from "@/types/inertia"

interface DeviceRow {
  id: number
  name: string
  activated_at: string
}

type LicenseProps = PageProps<{
  license: {
    id: number
    key: string
    status: string
    plan_type: string
    download_url: string | null
    version: string | null
    devices: DeviceRow[]
    device_limit: number
  } | null
  is_owner: boolean
  has_account: boolean
}>

const TABS = [
  { key: "license", label: "License" },
  { key: "devices", label: "Devices" },
]

export default function LicenseShow() {
  const { props } = usePage<LicenseProps>()
  const { license } = props
  const [tab, setTab] = useState("license")

  return (
    <>
      <Head title="My License">
        <meta name="description" content="Your Flywrite license key, download link, and activated devices." />
        <meta property="og:title" content="My License" />
        <meta property="og:description" content="Your Flywrite license key, download link, and activated devices." />
      </Head>
      <AccountShell>
        <PageHeader
          title="My License"
          description="Your license key, app download, and activated devices."
          tabs={
            license ? <SubNav items={TABS} active={tab} onChange={setTab} ariaLabel="License sections" /> : undefined
          }
        />

        {!license ? (
          !props.has_account ? (
            <div className="callout mt-8 text-center">
              <h2>No plan yet</h2>
              <p className="mt-2 text-sm text-ink-muted">
                Get Flywrite for yourself or your team — annual seats with volume pricing.
              </p>
              <p className="mt-4">
                <Link href="/buy" className="btn btn-primary">
                  See pricing
                </Link>
              </p>
            </div>
          ) : (
            <div className="callout mt-8">
              <p className="text-sm text-ink-body">
                You don't have a seat assigned yet.{" "}
                {props.is_owner ? (
                  <>
                    Assign yourself one from <Link href="/account/seats">Seats & Team</Link>.
                  </>
                ) : (
                  "Ask your account owner to assign you one."
                )}
              </p>
            </div>
          )
        ) : tab === "license" ? (
          <>
            <div className="callout mt-8">
              <div className="flex items-center justify-between">
                <h2>License key</h2>
                <LicenseStatusBadge status={license.status} />
              </div>
              <div className="mt-3">
                <CopyField value={license.key} ariaLabel="Copy license key" />
              </div>
              <p className="mt-2 text-xs text-ink-muted">
                {license.plan_type === "lifetime"
                  ? "Lifetime license — never expires."
                  : "Valid while your team's subscription is active."}{" "}
                Paste this into Flywrite when it asks for your license.
              </p>
              {license.download_url && (
                <p className="mt-4 flex items-center gap-3">
                  <a href={license.download_url} className="btn btn-primary btn-small">
                    Download Flywrite for Mac
                  </a>
                  {license.version && (
                    <span className="text-xs text-ink-muted">Version {license.version}</span>
                  )}
                </p>
              )}
            </div>
          </>
        ) : (
          <div className="callout mt-8">
            {license.devices.length >= license.device_limit ? (
              <p className="text-sm text-ink-body">
                You've activated Flywrite on the maximum number of devices for one license.
                Deactivate a device to make room, or purchase additional seats to activate
                Flywrite on more devices.
              </p>
            ) : (
              <p className="text-sm text-ink-muted">
                Devices where Flywrite is activated with this license key.
              </p>
            )}
            {license.devices.length > 0 && (
              <ul className="mt-4 divide-y divide-hairline rounded-lg border border-hairline">
                {license.devices.map((device) => (
                  <li
                    key={device.id}
                    className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <p className="text-sm text-ink-body">{device.name}</p>
                      <p className="text-xs text-ink-muted">Activated {device.activated_at}</p>
                    </div>
                    <div className="flex gap-2">
                      <RenameDeviceDialog device={device} />
                      <DeactivateDeviceDialog device={device} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </AccountShell>
    </>
  )
}

function RenameDeviceDialog({ device }: { device: DeviceRow }) {
  const [open, setOpen] = useState(false)
  const form = useForm({ name: device.name })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.patch(`/account/devices/${device.id}`, {
      preserveScroll: true,
      onSuccess: () => setOpen(false),
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          Rename
        </Button>
      </DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Rename device</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor={`device-name-${device.id}`}>Device name</label>
            <Input
              id={`device-name-${device.id}`}
              required
              value={form.data.name}
              onChange={(e) => form.setData("name", e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={form.processing}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function DeactivateDeviceDialog({ device }: { device: DeviceRow }) {
  const [open, setOpen] = useState(false)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          Deactivate
        </Button>
      </DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Deactivate {device.name}?</DialogTitle>
          <DialogDescription>
            This deactivates Flywrite on this device so you can activate it on another.
            Each device can be deactivated once every 30 days.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Keep it
          </Button>
          <Button
            variant="danger"
            onClick={() =>
              router.post(`/account/devices/${device.id}/deactivate`, {}, {
                preserveScroll: true,
                onSuccess: () => setOpen(false),
              })
            }
          >
            Deactivate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
